import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('adminToken');
      const savedAdmin = localStorage.getItem('adminData');
      
      if (token && savedAdmin) {
        try {
          setAdmin(JSON.parse(savedAdmin));
          // Verify token is still valid
          const response = await authAPI.getMe();
          setAdmin(response.data);
          localStorage.setItem('adminData', JSON.stringify(response.data));
        } catch (error) {
          console.error('Token validation failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { token, ...adminData } = response.data;
      
      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminData', JSON.stringify(adminData));
      setAdmin(adminData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    setAdmin(null);
  };

  const hasPermission = (requiredRole) => {
    if (!admin) return false;
    
    const roleHierarchy = {
      super_admin: 6,
      finance_admin: 5,
      risk_analyst: 4,
      kyc_officer: 3,
      marketing_admin: 2,
      support_admin: 1,
    };
    
    // Super admin has all permissions
    if (admin.role === 'super_admin') return true;
    
    // Check if admin's role level is >= required role level
    return roleHierarchy[admin.role] >= roleHierarchy[requiredRole];
  };

  const value = {
    admin,
    loading,
    login,
    logout,
    hasPermission,
    isAuthenticated: !!admin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
